// claim-parts.tsx: the pieces of a shared item U25 draws, in U20's tokens (screens/2026-09-18-conor-call/
// ContractReturn.html): the "Also with" row with an away colleague dashed (states 1 and 5), and the
// one awareness line naming who has it (state 3), which carries "I'll take it instead". The card's
// own states (2, "Yours"; 4, the warning) are drawn by ItemCard.tsx from claim.ts's words.

import { Chip } from './parts/primitives.js';
import { ActionButton } from './parts/ActionButton.js';
import { useState } from 'react';
import { ApiError, Unauthenticated, triage, type PassItem } from './api.js';
import { alsoWith, claimWords, firstName, theirLine } from './claim.js';
import './claim.css';

const TONE: Record<'bad' | 'you' | 'away' | 'plain', string> = { bad: 'bad', you: 'you', away: 'away', plain: '' };

/** The state pills a shared card wears in its meta row (U25). */
export function ClaimPills({ item }: { item: PassItem }) {
  const w = claimWords(item);
  if (!w) return null;
  return <>{w.pills.map((p) => <Chip key={p.text} className={TONE[p.tone]} data-claim-pill={p.tone}>{p.text}</Chip>)}</>;
}

/** "Also with Conor · Will · away" (U25 state 1): people, named, whether or not they sign in; away is dashed, never hidden. */
export function AlsoWith({ item }: { item: PassItem }) {
  const people = alsoWith(item);
  const w = claimWords(item);
  if (people.length === 0 && !w?.line) return null;
  return (
    <div className="meta also-with" data-claim={w?.state ?? ''}>
      {people.length > 0 ? <span>Also with</span> : null}
      {people.map((p) => (
        <Chip key={`${p.principal_id ?? p.name}`} className={p.away ? 'away' : ''} title={p.principal_id ? undefined : 'no Cobrain login yet: named, and cannot take it'}>
          {firstName(p.name)}{p.away ? ' · away' : ''}
        </Chip>
      ))}
      {w?.line ? <span className="claim-said">{w.line}</span> : null}
    </div>
  );
}

/**
 * U25 state 3: taken by a colleague, the item is one line in the awareness strip naming them, until
 * the task is done. "Open" goes to the item; "I'll take it instead" takes it over and tells them.
 */
export function ClaimLine({ item, open, onUpdate, onGone }: { item: PassItem; open: (item: PassItem) => void; onUpdate: (item: PassItem) => void; onGone: (id: string) => void }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const line = theirLine(item);
  if (!line) return null;
  const instead = item.optionsOffered.find((o) => (o.kind as string) === 'take' && o.params?.instead === true);
  const takeInstead = async () => {
    if (!instead) return;
    setBusy(true); setError(null);
    try {
      const r = await triage.decide(item.id, { store: item.store, action: 'accept', optionKey: instead.key });
      if (r.item) onUpdate({ ...r.item, private: item.private, store: item.store }); else onGone(item.id);
    } catch (e: unknown) {
      if (e instanceof Unauthenticated) { window.location.href = '/auth/login'; return; }
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="claim-line" data-item={item.id} data-claim="theirs">
      {item.who ? <Chip>{item.who}</Chip> : null}
      <span><strong>{line.who}</strong> {line.what} · {line.when}</span>
      <span className="claim-acts">
        <ActionButton reading type="button" className="link inline" onClick={() => open(item)}>Open</ActionButton>
        {instead ? <ActionButton type="button" className="opt small" disabled={busy} onClick={() => void takeInstead()}>{instead.label}{instead.note ? <small>{instead.note}</small> : null}</ActionButton> : null}
      </span>
      {error ? <p className="error">{error}</p> : null}
    </div>
  );
}
